/**
 * Utility functions for managing default date range values in content search filters
 * All dates are handled in UTC
 */

/**
 * Gets the default date range for content search filters in UTC
 * - endDate: current time in UTC
 * - startDate: exactly 30 days prior to current time in UTC
 *
 * @returns Object with startDate and endDate in datetime-local format (UTC)
 */
export function getDefaultDateRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const thirtyDaysAgo = new Date(now);

  thirtyDaysAgo.setUTCDate(now.getUTCDate() - 30);

  return {
    startDate: thirtyDaysAgo.toISOString().slice(0, 16),
    endDate: now.toISOString().slice(0, 16),
  };
}

/**
 * Checks if the provided date range matches the default date range
 * Used to determine if date filters should be excluded from URL params and badges
 *
 * @param startDate - The start date to check
 * @param endDate - The end date to check
 * @returns true if the dates match the default range (within 1 minute tolerance)
 */
export function isDefaultDateRange(startDate?: string, endDate?: string): boolean {
  if (!startDate || !endDate) {
    return false;
  }

  const defaultRange = getDefaultDateRange();

  // Parse dates for comparison (treat as UTC)
  const providedStart = new Date(`${startDate}Z`);
  const providedEnd = new Date(`${endDate}Z`);
  const defaultStart = new Date(`${defaultRange.startDate}Z`);
  const defaultEnd = new Date(`${defaultRange.endDate}Z`);

  // Allow 1 minute tolerance for comparison (60000 ms)
  const tolerance = 60000;

  const startMatches = Math.abs(providedStart.getTime() - defaultStart.getTime()) <= tolerance;
  const endMatches = Math.abs(providedEnd.getTime() - defaultEnd.getTime()) <= tolerance;

  return startMatches && endMatches;
}

/**
 * Checks if the provided date range appears to be a default range that should be refreshed
 * This checks if the dates are approximately 30 days apart, indicating they were likely
 * set as defaults and should be updated to "now" when searching
 *
 * @param startDate - The start date to check
 * @param endDate - The end date to check
 * @returns true if the dates appear to be default dates that should be refreshed
 */
export function shouldRefreshDateRange(startDate?: string, endDate?: string): boolean {
  if (!startDate || !endDate) {
    return false;
  }

  // Parse dates as UTC
  const start = new Date(`${startDate}Z`);
  const end = new Date(`${endDate}Z`);

  // Calculate the difference in days
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // If it's approximately 30 days (29-31 days to account for different month lengths)
  // and the end date is close to "now" (within 30 minutes), consider it a default range
  const now = new Date();
  const timeSinceEnd = Math.abs(now.getTime() - end.getTime());
  const thirtyMinutes = 30 * 60 * 1000;

  return diffDays >= 29 && diffDays <= 31 && timeSinceEnd <= thirtyMinutes;
}
